import * as React from "react";
import {
  ButtonToolbar,
  Button,
  OverlayTrigger,
  Form,
  DropdownButton,
  Dropdown,
  Card,
} from "react-bootstrap";
import ReactDatetime from "react-datetime";
import moment from "moment";
type Moment = moment.Moment;
import "moment/dist/locale/ja";
import I18n from "lib/I18n";
import tooltips from "../../lib/tooltips";
import style from "./style.module.css";
import { envColors, timeZones } from "../../constants";
import { BsFillTrashFill } from "react-icons/bs";
import { getStore } from "../../store";
import { observer } from "mobx-react-lite";

const TIME_FORMAT = "YYYY-MM-DD HH:00";
function clampMoment(time: string | Moment): Moment {
  return (typeof time === "string" ? moment(time) : time).set({
    minute: 0,
    second: 0,
    millisecond: 0,
  });
}
function formatMoment(time: string | Moment): string {
  return clampMoment(time).format(TIME_FORMAT);
}
function isValidTime(time?: string | Moment): boolean {
  return moment(time, TIME_FORMAT, true).isValid();
}

export const S3Delete = observer(() => {
  const { env, s3Delete } = getStore();

  const onChangeTimeFrom = (
    time: React.FocusEvent<unknown> | string | Moment,
  ) => {
    if (typeof time === "string" || moment.isMoment(time)) {
      s3Delete.timeFrom = formatMoment(time);
    }
  };

  const onChangeTimeTo = (
    time: React.FocusEvent<unknown> | string | Moment,
  ) => {
    if (typeof time === "string" || moment.isMoment(time)) {
      s3Delete.timeTo = formatMoment(time);
    }
  };

  const onDeleteFromS3Click = () => {
    if (env.isLp1()) {
      return;
    }
    s3Delete.deleteFromS3();
  };

  const onChangeEnv = (value: string | null) => {
    if (value == null) {
      return;
    }
    env.selected = value;
  };

  const onSelectTimeZone = (value: string | null) => {
    if (value == null) {
      return;
    }
    s3Delete.timeZone = value as "JST" | "UTC";
  };

  const envColor = (value: string) => {
    return envColors[value] || "primary";
  };

  const timeZoneDescription = (timeZone: string) => {
    return timeZones[timeZone];
  };

  const isValidTimeFrom = () => {
    return isValidTime(s3Delete.timeFrom);
  };

  const isValidTimeTo = () => {
    return isValidTime(s3Delete.timeTo);
  };

  const isValidDates = () => {
    return isValidTimeFrom() && isValidTimeTo();
  };

  const renderEnvItems = () => {
    return env.list.map((value) => (
      <Dropdown.Item key={value} eventKey={value}>
        {value}
      </Dropdown.Item>
    ));
  };

  const renderTimeZones = () => {
    return Object.keys(timeZones).map((value) => (
      <Dropdown.Item key={value} eventKey={value}>
        {timeZoneDescription(value)}
      </Dropdown.Item>
    ));
  };

  const renderDateFilter = () => {
    const { timeFrom, timeTo } = s3Delete;
    return (
      <Form>
        <ButtonToolbar>
          <Dropdown className="me-2" onSelect={onChangeEnv}>
            <Dropdown.Toggle
              id="envdropdown"
              variant={envColor(env.selected)}
              title={env.selected}
              key={env.selected}
            >
              {env.selected}
            </Dropdown.Toggle>
            <Dropdown.Menu>{renderEnvItems()}</Dropdown.Menu>
          </Dropdown>
          <OverlayTrigger placement="top" overlay={tooltips.time_zone}>
            <DropdownButton
              id="tzdropdown"
              className="me-2"
              title={s3Delete.timeZone}
              key={s3Delete.timeZone}
              onSelect={onSelectTimeZone}
            >
              {renderTimeZones()}
            </DropdownButton>
          </OverlayTrigger>
          <Form.Group className="me-2">
            <OverlayTrigger placement="top" overlay={tooltips.time_from}>
              <ReactDatetime
                locale={I18n.locale}
                inputProps={{ placeholder: "YYYY-MM-DD HH:00" }}
                initialValue={timeFrom ? moment(timeFrom).toDate() : undefined}
                onClose={onChangeTimeFrom}
                dateFormat="YYYY-MM-DD"
                timeFormat="HH:00"
                strictParsing
              />
            </OverlayTrigger>
          </Form.Group>
          <Form.Group className="me-2">
            <OverlayTrigger placement="top" overlay={tooltips.time_to}>
              <ReactDatetime
                locale={I18n.locale}
                inputProps={{ placeholder: "YYYY-MM-DD HH:00" }}
                initialValue={timeTo ? moment(timeTo).toDate() : undefined}
                onClose={onChangeTimeTo}
                dateFormat="YYYY-MM-DD"
                timeFormat="HH:00"
                strictParsing
              />
            </OverlayTrigger>
          </Form.Group>
          <OverlayTrigger
            delay={200}
            placement="top"
            overlay={tooltips.delete_from_s3}
          >
            <Button
              className="copy-button me-2"
              onClick={onDeleteFromS3Click}
              disabled={!isValidDates() || env.isLp1()}
            >
              <BsFillTrashFill />
            </Button>
          </OverlayTrigger>
        </ButtonToolbar>
        <ul className="text-danger">
          {isValidDates() || (
            <li>{I18n.t("event_master_editor.errors.invalid_date")}</li>
          )}
          {!env.isLp1() || (
            <li>{I18n.t("event_master_editor.errors.delete_from_s3_lp1")}</li>
          )}
        </ul>
      </Form>
    );
  };

  return (
    <div className={style.root}>
      <Card>
        <Card.Header>
          {I18n.t("event_master_editor.messages.s3_delete_desc")}
        </Card.Header>
        <Card.Body>
          <div>{renderDateFilter()}</div>
        </Card.Body>
      </Card>
    </div>
  );
});
